<template>
  <div class="trends-report-roc-popup-chart" :class="fromReport ? 'align-left' : ''" :style="chartStyle">
    <div :class="getClassName">
      <div :id="chartID"></div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'TrendsReportROCPopupChart',
    components: {},
    props: {
      rocData: {
        type: Object,
        required: true,
      },
      border: {
        type: Boolean,
        default: false,
      },
      fromReport: {
        type: Boolean,
        default: false,
      },
      width: {
        type: Number, 
        required: false,
      },
    },
    data: () => ({
      chartLayout: {},
      forecastUMRDataTrace: {},
      forecastLMRDataTrace: {},
      primaryHistoricDataTrace: {},
      previousHistoricDataTrace: {},
      secondaryHistoricDataTrace: {},
      previousForecastDataTrace: {},
      traces: [],
      chartID: (Math.random() + 1).toString(36).substring(3),
      fontSize: 14,
    }),
    mounted() {
      if (this.fromReport) {
        this.fontSize = 17;
      }
      if(this.width) {
        this.fontSize = 10;
      }
      this.buildChartData();
    },
    updated() {
      this.buildChartData();
    },    
    computed: {
      getClassName() {
        let className = '';
        if (this.border) className += 'trends-report-roc-popup-chart__border ';
        if (this.fromReport) {
          className += 'trends-report-roc-popup-chart__from-report ';
        } else {
          className += 'trends-report-roc-popup-chart__set-dimensions ';
        }
        return className;
      },
      chartStyle() {
        if(!this.width) return;
        return {
          width: this.width + 'px',
          height: 'auto',
        };
      }
    },
    methods: {
      buildChartData() {
        let forecastUMRData = [];
        let forecastUMRMonthYear = [];
        let showForecastUMRData = false;
        let UMRMode = 'lines+markers';

        let forecastLMRData = [];
        let forecastLMRMonthYear = [];
        let showForecastLMRData = false;
        let LMRmode = 'lines+markers';

        let primaryHistoricData = [];
        let primaryHistoricMonthYear = [];
        let showPrimaryHistoricData = false;

        let previousHistoricData = [];
        let previousHistoricMonthYear = [];
        let showPreivousHistoricData = false;

        let previousForecastData = [];
        let previousForecastMonthYear = [];
        let showPreviousForecastData = false;

        let secondaryHistoricData = [];
        let secondaryHistoricMonthYear = [];
        let showSecondaryHistoricData = false;

        let UMRCount = 0;
        for (
          let index = 0;
          index < this.rocData.ForecastUMRData.length;
          index++
        ) {
          const element = this.rocData.ForecastUMRData[index];
          forecastUMRMonthYear.push(new Date(element.MonthYear));
          forecastUMRData.push(element.DataPoint);
          if (element.DataPoint.trim() != '') {
            UMRCount++;
            showForecastUMRData = true;
          }
        }
        if (UMRCount > 1) {
          UMRMode = 'lines';
        }

        let LMRCount = 0;
        for (
          let index = 0;
          index < this.rocData.ForecastLMRData.length;
          index++
        ) {
          const element = this.rocData.ForecastLMRData[index];
          forecastLMRMonthYear.push(new Date(element.MonthYear));
          forecastLMRData.push(element.DataPoint);
          if (element.DataPoint.trim() != '') {
            LMRCount++;
            showForecastLMRData = true;
          }
        }
        if (LMRCount > 1) {
          LMRmode = 'lines';
        }

        for (
          let index = 0;
          index < this.rocData.PrimaryHistoricData.length;
          index++
        ) {
          const element = this.rocData.PrimaryHistoricData[index];
          primaryHistoricMonthYear.push(new Date(element.MonthYear));
          primaryHistoricData.push(element.DataPoint);
          if (element.DataPoint.trim() != '') {
            showPrimaryHistoricData = true;
          }
        }

        for (
          let index = 0;
          index < this.rocData.PreviousPrimaryHistoricData.length;
          index++
        ) {
          const element = this.rocData.PreviousPrimaryHistoricData[index];
          previousHistoricMonthYear.push(new Date(element.MonthYear));
          previousHistoricData.push(element.DataPoint);
          if (element.DataPoint.trim() != '') {
            showPreivousHistoricData = true;
          }
        }

        for (
          let index = 0;
          index < this.rocData.SecondaryHistoricData.length;
          index++
        ) {
          const element = this.rocData.SecondaryHistoricData[index];
          secondaryHistoricMonthYear.push(new Date(element.MonthYear));
          secondaryHistoricData.push(element.DataPoint);
          if (element.DataPoint.trim() != '') {
            showSecondaryHistoricData = true;
          }
        }

        for (
          let index = 0;
          index < this.rocData.PreviousForecastData.length;
          index++
        ) {
          const element = this.rocData.PreviousForecastData[index];
          previousForecastMonthYear.push(new Date(element.MonthYear));
          previousForecastData.push(element.DataPoint);
          if (element.DataPoint.trim() != '') {
            showPreviousForecastData = true;
          }
        }

        this.primaryHistoricDataTrace = {
          type: 'scatter',
          mode: 'scatter',
          x: primaryHistoricMonthYear,
          y: primaryHistoricData,
          name: this.rocData.PrimaryMetric,
          line: {
            width: 2,
            color: '#4e79cb',
          },
          yaxis: 'y',
          xaxis: 'x',
          visible: showPrimaryHistoricData,
          customdata: primaryHistoricData,
          hovertemplate: `%{customdata:.1f}`,          
        };

        this.secondaryHistoricDataTrace = {
          type: 'scatter',
          mode: 'scatter',
          x: secondaryHistoricMonthYear,
          y: secondaryHistoricData,
          name: this.rocData.SecondaryMetric,
          line: {
            width: 2,
            color: '#c7c7c7',
          },
          yaxis: 'y2',
          xaxis: 'x',
          visible: showSecondaryHistoricData,
          customdata: secondaryHistoricData,
          hovertemplate: `%{customdata:.1f}`,          
        };

        this.forecastUMRDataTrace = {
          mode: UMRMode,
          line: {
            dash: 'dashdot',
            width: 2,
            color: '#4e79cb',
          },
          x: forecastUMRMonthYear,
          y: forecastUMRData,
          name: `${this.rocData.PrimaryMetric} Forecast Range`,
          yaxis: 'y',
          xaxis: 'x',
          visible: showForecastUMRData,
          showlegend: false,
          customdata: forecastUMRData,
          hovertemplate: `%{customdata:.1f}`,          
        };

        this.forecastLMRDataTrace = {
          mode: LMRmode,
          line: {
            dash: 'dashdot',
            width: 2,
            color: '#4e79cb',
          },
          x: forecastLMRMonthYear,
          y: forecastLMRData,
          name: `${this.rocData.PrimaryMetric} Forecast Range`,
          yaxis: 'y',
          xaxis: 'x',
          visible: showForecastLMRData,
          customdata: forecastLMRData,
          hovertemplate: `%{customdata:.1f}`,          
        };

        this.previousHistoricDataTrace = {
          type: 'scatter',
          mode: 'scatter',
          x: previousHistoricMonthYear,
          y: previousHistoricData,
          name: `${this.rocData.PrimaryMetric} Pre-Revision`,
          line: {
            width: 2,
            color: '#ed7d31',
          },
          yaxis: 'y',
          xaxis: 'x',
          visible: showPreivousHistoricData,
          customdata: previousHistoricData,
          hovertemplate: `%{customdata:.1f}`,          
        };

        this.previousForecastDataTrace = {
          mode: 'lines',
          x: previousForecastMonthYear,
          y: previousForecastData,
          name: `${this.rocData.PrimaryMetric} Pre-Revision Forecast`,
          line: {
            dash: 'dashdot',
            width: 2,
            color: '#ed7d31',
          },
          yaxis: 'y',
          xaxis: 'x',
          visible: showPreviousForecastData,
          customdata: previousHistoricData,
          hovertemplate: `%{customdata:.1f}`,          
        };

        this.traces = [
          this.primaryHistoricDataTrace,
          this.secondaryHistoricDataTrace,
          this.forecastLMRDataTrace,
          this.forecastUMRDataTrace,
          this.previousHistoricDataTrace,
          this.previousForecastDataTrace,
        ];

        this.chartLayout = {
          autosize: true,
          showlegend: true,
          legend: {
            orientation: 'h',
            y: 1.1,
            x: 0.5,
            xanchor: 'center',
            yanchor: 'middle',
            valign: 'middle',
            traceorder: 'normal',
            bgcolor: 'rgba(0,0,0,0)',
            bordercolor: '#444',
            borderwidth: 0,
            font: {
              size: this.fontSize,
            },
          },
          yaxis: {
            title: '',
            zeroline: true,
            mirror: true,
            autotick: true,
            automargin: true,
            showgrid: false,
            showline: true,
            ticks: 'outside',
            overlaying: 'y2',
          },
          yaxis2: {
            title: '',
            zeroline: false,
            autotick: true,
            automargin: true,
            showgrid: false,
            showline: true,
            ticks: 'outside',
            side: 'right',
          },
          xaxis: {
            type: 'date',
            dtick: 'M12',
            ticks: 'outside',
            automargin: true,
            showgrid: false,
            showline: true,
            tickformat: '%Y',
            hoverformat: '%b %Y',
          },
          margin: {
            t: 50,
            l: 50,
            r: 50,
            b: 50,
          },
          dragmode: false,         
        };

        if(this.width) {
          this.chartLayout.height = this.width / 1.10576923;
          this.chartLayout.width = this.width;
        }

        this.plotChart();
      },
      plotChart() {
        Plotly.react(this.chartID, this.traces, this.chartLayout, {
          responsive: true,
          staticPlot: false,
          scrollZoom: false, 
          editable: false, 
          displaylogo: false,
          displayModeBar: false,
          doubleClick: false, 
        }).then((chartData) => {
          this.adjustChart();
          document.getElementById(this.chartID).on('plotly_legendclick', () => {
            return false;
          })
        });
      },
      adjustChart() {
        let layoutObject = this.getTableRanges();
        Plotly.update(this.chartID, null, layoutObject);
      },
      getTableRanges: function () {
        let y1_min = this.rocData.MinY1;
        let y1_max = this.rocData.MaxY1;
        let y2_min = this.rocData.MinY2;
        let y2_max = this.rocData.MaxY2;

        return {
          'yaxis.range': [y1_min, y1_max],
          'yaxis2.range': [y2_min, y2_max],
          'xaxis.tickfont': { size: this.fontSize },
          'yaxis.tickfont': { size: this.fontSize },
          'yaxis2.tickfont': { size: this.fontSize },
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .trends-report-roc-popup-chart {
    display: flex;
    align-content: center;
    justify-content: center;
    align-items: center;
    &__border {
      overflow: hidden;
      border-radius: 20px;
      border: 2px solid black;
    }
    &__set-dimensions {
      height: 100%;
      width: 100%;
    }
    &__from-report {
      height: unset;
      width: unset;
    }
  }
  .trends-report-roc-popup-chart.align-left {
      display: flex;
      align-content: flex-start;
      justify-content: flex-start;
      align-items: center;
    }
</style>
